<template>
  <TransitionRoot as="template" :show="open" @update:model-value="open =!open" >
    <Dialog class="relative z-10" @close="open = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto"  @click="handleClick">
        <div class="flex min-h-full items-start justify-center p-4 text-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8"
              :class="{ 'sm:max-w-lg sm:w-full': !width, [width]: width }">
              <div
                v-if="!noCloseButton"
                class="absolute right-0 top-0 pr-4 pt-4"
              >
                <button type="button" class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none" @click="open = false">
                  <span class="sr-only">Close</span>
                  <span class="h-6 w-6 i-heroicons-x-mark" aria-hidden="true" />
                </button>
              </div>
              <slot>
                <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div>
                    <slot name="title">
                      <DialogTitle as="h3" class="text-slate font-semibold leading-6 text-gray-900">{{ title }}</DialogTitle>
                    </slot>

                    <slot name="body">
                      <div class="mt-2">
                        <p class="text-sm text-gray-500">{{ body }}</p>
                      </div>
                    </slot>
                  </div>
                </div>
                <slot name="footer">
                  <div class="bg-white py-3 flex px-6 justify-end gap-2">
                    <BaseButton
                      v-if="closeButtonText"
                      variant="primary"
                      :text="closeButtonText"
                      @click="open = false"
                    />
                  </div>
                </slot>
              </slot>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import BaseButton from '../base/BaseButton.vue'

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  body: {
    type: String,
    default: ''
  },
  closeButtonText: {
    type: String,
    default: ''
  },
  value: {
    type: Boolean,
    default: false
  },
  width: {
    type: String,
    default: ''
  },
  noEscape: {
    type: Boolean,
    default: false
  },
  noBackdrop: {
    type: Boolean,
    default: false
  },
  noCloseButton: {
    type: Boolean,
    default: false
  }
})

const open = defineModel({
  type: Boolean,
  default: false
})
const emit = defineEmits(['close', 'open'])


watch(() => props.value, (value) => {
  open.value = value
})

watch(() => open.value, (value) => {
  open.value = value
  if (value) {
    emit('open')
    document.addEventListener('keydown', keyDonwListener)
  } else {
    emit('close')
    document.removeEventListener('keydown', keyDonwListener)
    document.body.classList.remove('overflow-hidden')
  }
})

const handleClick = () => {
  if (props.noBackdrop) {
    open.value = true
    document.body.classList.add('overflow-hidden')
  }
}

const keyDonwListener = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.noEscape) {
    event.preventDefault()
    open.value = true
  }
}
</script>
